import { LogLevel } from './logger'

const getEnv = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[key]
  if (!value && defaultValue === undefined) {
    throw new Error(`Missing required environment variable: ${key}`)
  }
  return value || defaultValue!
}

const getBoolean = (key: string, defaultValue = false): boolean => {
  const value = import.meta.env[key]
  return value ? value.toLowerCase() === 'true' : defaultValue
}

const getNumber = (key: string): number => {
  const value = getEnv(key)
  const num = Number(value)
  if (isNaN(num)) {
    throw new Error(`Invalid number for environment variable: ${key}`)
  }
  return num
}

const getLogLevel = (key: string, defaultValue: LogLevel = 'error'): LogLevel => {
  const value = getEnv(key, defaultValue)
  const validLevels: LogLevel[] = ['error', 'warn', 'info', 'debug']
  if (!validLevels.includes(value as LogLevel)) {
    throw new Error(`Invalid log level: ${value}. Must be one of: ${validLevels.join(', ')}`)
  }
  return value as LogLevel
}

const sentryEnabled = getBoolean('VITE_SENTRY_ENABLED', false)
const googleAnalyticsId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID

export const config = Object.freeze({
  env: getEnv('VITE_ENV', 'development') as 'development' | 'uat' | 'production',
  logLevel: getLogLevel('VITE_LOG_LEVEL', 'error'),

  sentry: sentryEnabled
    ? {
        dsn: getEnv('VITE_SENTRY_DSN'),
        sampleRate: getNumber('VITE_SENTRY_TRACES_SAMPLE_RATE'),
      }
    : undefined,

  cdn: {
    mediaBaseUrl: getEnv('VITE_CDN_MEDIA_BASE_URL'),
    publicFileUrl: getEnv('VITE_CDN_PUBLIC_FILE_URL'),
  },

  posthog: {
    // Empty key = product analytics off for the build (see posthog-service). Optional on
    // purpose: the e2e builds and a developer without the env should run without opening a
    // socket to PostHog.
    apiKey: (import.meta.env.VITE_POSTHOG_API_KEY as string | undefined) || undefined,
    host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com',
  },

  googleAnalytics: googleAnalyticsId
    ? {
        measurementId: googleAnalyticsId,
        debug: getBoolean('VITE_GOOGLE_ANALYTICS_DEBUG', false),
      }
    : undefined,
})
